import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';

/**
 * `sm`   = 30px compact
 * `md`   = 34px default field height (--h-field)
 * `lg`   = 40px spacious
 * `auth` = 46px for login/auth screens
 */
export type SelectSize = 'sm' | 'md' | 'lg' | 'auth';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: SelectSize;
  invalid?: boolean;
  className?: string;
  children?: ReactNode;
}

const SIZE: Record<SelectSize, string> = {
  sm: 'h-[1.875rem] px-2 text-body-sm', // 30px compact
  md: 'h-[var(--h-field)] px-[var(--field-pad)] text-body-sm', // 34px default
  lg: 'h-10 px-[var(--field-pad)] text-body', // 40px spacious
  auth: 'h-[2.875rem] px-[var(--field-pad)] text-body', // 46px login/auth screens
};

const BASE =
  'w-full rounded-control border bg-surface text-primary border-border ' +
  'focus-visible:outline-none focus-visible:border-ring-focus ' +
  'focus-visible:ring-[3px] focus-visible:ring-ring-focus/20 ' +
  'disabled:opacity-disabled disabled:cursor-not-allowed';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid = false, className, children, ...rest },
  ref,
) {
  const classes = [
    BASE,
    SIZE[size],
    invalid
      ? 'border-danger focus-visible:ring-[3px] focus-visible:ring-danger/20 focus-visible:border-danger'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <select ref={ref} aria-invalid={invalid || undefined} className={classes} {...rest}>
      {children}
    </select>
  );
});
